import React, { useState, useEffect } from 'react';
import GlassyModal from 'components/ui/GlassyModal';
import Icon from 'components/AppIcon';
import { useAuth } from 'contexts/AuthContext';
import { db } from '../../firebaseClient'; // Firebase services
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';

const ReferralCodeGenerator = ({ isOpen, onClose }) => {
  const { user, profile } = useAuth(); // Get user and profile from Firebase
  const [loading, setLoading] = useState(true);
  const [codeExists, setCodeExists] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [requestingCode, setRequestingCode] = useState(false);

  // Fetch existing referral code on component mount
  useEffect(() => {
    if (isOpen && user) {
      fetchReferralCode();
    }
  }, [isOpen, user]);

  const fetchReferralCode = async () => {
    setLoading(true);
    setError('');

    try {
      // Check if user already has a referral code
      const referralCodeDocRef = doc(db, 'referral_codes', user.uid);
      const referralCodeDoc = await getDoc(referralCodeDocRef);

      if (referralCodeDoc.exists()) {
        // User already has a referral code
        setReferralCode(referralCodeDoc.data().code);
        setCodeExists(true);
      } else {
        // No existing code
        setCodeExists(false);
      }
    } catch (error) {
      console.error('Error fetching referral code:', error);
      setError('Failed to fetch your referral code. Please try again.');
      setCodeExists(false);
    } finally {
      setLoading(false);
    }
  };

  const requestReferralCode = async () => {
    setRequestingCode(true);
    setError('');

    try {
      // In a real implementation, you would have a backend function to generate a unique code
      // and create a support ticket. For now, we'll simulate this by logging to the console.
      console.log('Referral code requested for user:', user.uid);

      // Record the request in the database
      await setDoc(doc(db, 'referral_requests', user.uid), {
        user_id: user.uid,
        email: user.email,
        name: profile?.full_name || user.displayName || '',
        created_at: serverTimestamp(),
        status: 'pending'
      });

      // Show success message
      setError('');
      alert('Your referral code request has been submitted! Our team will process your request shortly.');

    } catch (error) {
      console.error('Error requesting referral code:', error);
      setError('Failed to submit your referral code request. Please try again.');
    } finally {
      setRequestingCode(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(referralCode)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      })
      .catch(err => {
        console.error('Failed to copy:', err);
        setError('Failed to copy to clipboard. Please try manually selecting the code.');
      });
  };

  return (
    <GlassyModal
      isOpen={isOpen}
      onClose={onClose}
      title="Your Referral Code"
      icon="Gift"
      maxWidth="max-w-lg"
    >
      <div className="space-y-6">
        {error && (
          <div className="bg-red-500/20 border border-red-500/30 text-red-100 p-4 rounded-lg">
            <div className="flex items-start">
              <Icon name="AlertTriangle" size={20} className="mr-2 mt-0.5 flex-shrink-0" />
              <p>{error}</p>
            </div>
          </div>
        )}

        <div className="text-center">
          <p className="text-white/80 mb-6">
            Share your unique referral code with friends and earn rewards when they sign up!
          </p>

          {loading ? (
            <div className="flex justify-center items-center py-8">
              <Icon name="Loader2" size={32} className="animate-spin text-accent" />
              <span className="ml-3 text-white">Loading your referral code...</span>
            </div>
          ) : (
            <>
              <div className="bg-white/5 backdrop-blur-sm border border-white/20 rounded-xl p-6 mb-6">
                <h3 className="text-white/60 text-sm mb-2">Your Unique Referral Code</h3>

                {codeExists ? (
                  <>
                    <div className="flex items-center justify-center">
                      <div className="bg-white/10 rounded-lg px-6 py-4 text-2xl font-mono font-bold text-accent tracking-wider">
                        {referralCode}
                      </div>
                      <button
                        onClick={copyToClipboard}
                        className="ml-3 bg-white/10 hover:bg-white/20 p-3 rounded-lg transition-colors"
                        aria-label="Copy to clipboard"
                      >
                        {copied ? (
                          <Icon name="Check" size={20} className="text-green-400" />
                        ) : (
                          <Icon name="Copy" size={20} className="text-white" />
                        )}
                      </button>
                    </div>

                    {copied && (
                      <p className="text-green-400 text-sm mt-2">Copied to clipboard!</p>
                    )}
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center py-4">
                    <p className="text-white/80 mb-4">You don't have a referral code yet.</p>
                    <button
                      onClick={requestReferralCode}
                      disabled={requestingCode}
                      className="bg-accent hover:bg-accent-600 text-white px-6 py-3 rounded-lg flex items-center transition-colors"
                    >
                      {requestingCode ? (
                        <>
                          <Icon name="Loader2" size={18} className="animate-spin mr-2" />
                          Requesting...
                        </>
                      ) : (
                        <>
                          <Icon name="Send" size={18} className="mr-2" />
                          Request a Code
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>

              {codeExists && (
                <>
                  <div className="bg-white/5 backdrop-blur-sm border border-white/20 rounded-xl p-6">
                    <h3 className="text-white text-lg font-medium mb-4">How It Works</h3>
                    <div className="space-y-4">
                      <div className="flex items-start">
                        <div className="w-8 h-8 bg-accent/20 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                          <span className="text-accent font-bold">1</span>
                        </div>
                        <p className="text-white/80">Share your referral code with friends who need academic help</p>
                      </div>
                      <div className="flex items-start">
                        <div className="w-8 h-8 bg-accent/20 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                          <span className="text-accent font-bold">2</span>
                        </div>
                        <p className="text-white/80">When they sign up and use your code, they get 10% OFF their first order</p>
                      </div>
                      <div className="flex items-start">
                        <div className="w-8 h-8 bg-accent/20 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                          <span className="text-accent font-bold">3</span>
                        </div>
                        <p className="text-white/80">You earn 15% OFF your next assignment and gain VIP status</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                    <button
                      onClick={() => {
                        const text = `Hey! I've been using SOMO for academic help and it's been great. Use my referral code ${referralCode} to get 10% off your first order! Check them out at ${window.location.origin}`;
                        navigator.clipboard.writeText(text)
                          .then(() => {
                            alert('Referral message copied to clipboard! Paste it to share with your friends.');
                          })
                          .catch(err => {
                            console.error('Failed to copy:', err);
                            setError('Failed to copy message. Please try manually sharing your code.');
                          });
                      }}
                      className="px-6 py-3 bg-accent hover:bg-accent-600 rounded-lg text-white flex items-center"
                    >
                      <Icon name="Copy" size={18} className="mr-2" />
                      Copy Referral Message
                    </button>

                    <button
                      onClick={() => {
                        const text = `Check out SOMO for academic help! Use my referral code ${referralCode} to get 10% off your first order!`;
                        const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
                        window.open(url, '_blank');
                      }}
                      className="px-6 py-3 bg-green-500 hover:bg-green-600 rounded-lg text-white flex items-center"
                    >
                      <Icon name="Share2" size={18} className="mr-2" />
                      Share via WhatsApp
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </GlassyModal>
  );
};

export default ReferralCodeGenerator;

